import axios from 'axios';
import { ApiResponse, FrontendMenu, WorkflowMarket, WorkflowMarketHistory, SubTaskInfo, TaskListResponse, WorkflowModelMapping, UserPointsInfo, CallGroup, Announcement } from '../types';

interface RetryableAxiosRequestConfig {
  __retryCount?: number;
  __maxRetries?: number;
  __skipRetry?: boolean;
  method?: string;
  timeout?: number;
  url?: string;
}

// 创建axios实例
const api = axios.create({
  baseURL: (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001') + '/api',
  timeout: 180000, // 3分钟超时，适用于图片分析等耗时操作
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 添加认证token
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 网络错误重试工具函数
const isNetworkError = (error: any): boolean => {
  if (!error.response && error.code) {
    return ['ERR_NETWORK', 'ECONNABORTED', 'ERR_CONNECTION_RESET', 'ETIMEDOUT'].includes(error.code);
  }
  return false;
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    const config = error.config as RetryableAxiosRequestConfig | undefined;
    
    // Token过期处理：直接跳转登录页
    if (error.response?.status === 401 && !window.location.pathname.includes('/login')) {
      console.warn('Token已过期，请重新登录');
      localStorage.removeItem('authToken');
      localStorage.removeItem('userInfo');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // 自动重试：对所有请求类型的网络错误进行重试（香港服务器→大陆丢包容错）
    if (config?.__skipRetry) {
      return Promise.reject(error);
    }

    if (config && isNetworkError(error)) {
      config.__retryCount = config.__retryCount || 0;
      const maxRetries = config.__maxRetries || 12;

      if (config.__retryCount < maxRetries) {
        config.__retryCount++;
        const retryDelay = config.__retryCount * 2000; // 2s, 4s, 6s, 8s, 10s, 12s
        console.warn(`🔄 API 请求重试 (${config.__retryCount}/${maxRetries}): [${config.method?.toUpperCase()}] ${config.url}, ${retryDelay}ms 后重试`);
        await delay(retryDelay);
        return api(config);
      }
    }

    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// 菜单相关API
export const menuApi = {
  // 获取所有菜单
  getMenus: (): Promise<ApiResponse<FrontendMenu[]>> => 
    api.get('/menus'),
  
  // 获取菜单树结构
  getMenuTree: (): Promise<ApiResponse<FrontendMenu[]>> => 
    api.get('/menus/tree'),
};

// 工作流市场相关API
export const workflowMarketApi = {
  // 获取所有工作流市场项目（使用数据库数据，支持角色权限过滤）
  getProjects: (userId?: string, roleId?: string | null): Promise<ApiResponse<WorkflowMarket[]>> => {
    const params: any = {};
    if (userId) params.user_id = userId;
    if (roleId) params.role_id = roleId;
    return api.get('/workflow-market', { params });
  },
  
  // 获取所有工作流市场项目（原有方法，保持兼容性）
  getWorkflowMarkets: (): Promise<ApiResponse<WorkflowMarket[]>> => 
    api.get('/workflow-markets'),
  
  // 获取热门工作流（基于最近更新的对话记录）
  getPopularWorkflows: (userId: string): Promise<ApiResponse<WorkflowMarket[]>> => 
    api.get(`/workflow-markets/popular?user_id=${userId}`),
  
  // 根据ID获取项目详情
  getProjectById: (id: number): Promise<ApiResponse<WorkflowMarket>> => 
    api.get(`/workflow-markets/${id}`),
  
  // 根据路由slug获取项目
  getProjectBySlug: (slug: string): Promise<ApiResponse<WorkflowMarket>> => 
    api.get(`/workflow-markets/slug/${slug}`),
  
  // 执行工作流
  executeWorkflow: (projectId: number, parameters: Record<string, any>): Promise<ApiResponse<{ task_id: string }>> => 
    api.post(`/workflow-markets/${projectId}/execute`, { parameters }),
};

// 公告相关API
export const announcementApi = {
  // 获取首页公告列表
  getAnnouncements: (): Promise<ApiResponse<Announcement[]>> =>
    api.get('/announcements')
};

// 工作流历史记录相关API
export const workflowHistoryApi = {
  // 获取项目历史记录
  getProjectHistory: (projectId: number, userId: number): Promise<ApiResponse<WorkflowMarketHistory[]>> => 
    api.get(`/workflow-market/${projectId}/history?user_id=${userId}`),
  
  // 获取历史记录详情
  getHistoryDetail: (historyId: number): Promise<ApiResponse<WorkflowMarketHistory>> => 
    api.get(`/workflow-history/${historyId}`),
};

// 任务相关API
export const taskApi = {
  // 获取用户所有任务
  getUserTasks: (userId: number): Promise<ApiResponse<SubTaskInfo[]>> => 
    api.get(`/tasks/user/${userId}`),
  
  // 获取所有子任务（按用户ID过滤，parent_id = 0）
  getSubTasks: (userId?: string): Promise<ApiResponse<TaskListResponse>> => 
    api.get('/sub-tasks', { params: { user_id: userId, limit: 15000 } }),
  
  // 获取任务详情
  getTaskDetail: (taskId: string): Promise<ApiResponse<SubTaskInfo>> => 
    api.get(`/tasks/${taskId}`),
  
  // 获取任务状态
  getTaskStatus: (taskId: string): Promise<ApiResponse<{ status: string; progress: number }>> => 
    api.get(`/tasks/${taskId}/status`),
  
  // 获取云服务器信息
  getCloudServer: (cloudSrvId: string): Promise<ApiResponse<{ srv_name: string }>> => 
    api.get(`/cloud-servers/${cloudSrvId}`),
};

// 云服务器相关API
export const cloudServerApi = {
  // 获取云服务器列表
  getServerList: (): Promise<ApiResponse<any[]>> => 
    api.get('/cloud-servers'),
  
  // 获取部署配置
  getDeployConfig: (): Promise<ApiResponse<any>> => 
    api.get('/cloud-servers/deploy-config'),
  
  // 获取服务器状态
  getServerStatus: (cloudSrvId: string): Promise<ApiResponse<any>> =>
    api.get(`/cloud-servers/${cloudSrvId}/status`),
  
  // 销毁服务器
  destroyServer: (cloudSrvId: string): Promise<ApiResponse<any>> =>
    api.delete(`/cloud-servers/${cloudSrvId}`),
  
  // 部署服务器
  deployServer: (values: any): Promise<ApiResponse<any>> =>
    api.post('/cloud-servers/deploy', values),
  
  // 检查工作流状态
  checkWorkflowStatus: (): Promise<ApiResponse<any>> =>
    api.post('/cloud-servers/check-workflow-status'),
  
  // 重启ComfyUI进程
  restartComfyUI: (cloudSrvId: string): Promise<ApiResponse<any>> =>
    api.post('/cloud-servers/restart-comfyui', { cloudSrvId }),

  // 获取任务完成后自动销毁配置
  getAutoDestroyConfig: (): Promise<ApiResponse<{ config_value: '0' | '1' }>> =>
    api.get('/cloud-servers/auto-destroy-config'),

  // 保存任务完成后自动销毁配置
  updateAutoDestroyConfig: (configValue: '0' | '1'): Promise<ApiResponse<{ config_value: '0' | '1' }>> =>
    api.put('/cloud-servers/auto-destroy-config', { config_value: configValue }),
};

// 认证相关API
export const authApi = {
  // 检查任务权限（试用用户权限检查）
  checkTaskPermission: (): Promise<ApiResponse<{ canExecute: boolean; message: string; remainingTimes: number }>> =>
    api.get('/auth/check-permission'),
};

// 对话相关API
export const conversationApi = {
  // 获取工作流信息（包含component_template）
  getWorkflowInfo: (marketId: string): Promise<ApiResponse<any>> =>
    api.get(`/conversations/workflow-info/${marketId}`),
  
  // 发送消息（支持多张图片和异步任务）
  sendMessage: (data: { market_id: string; user_id: string; message: string; session_id?: string; image_urls?: string[] }): Promise<ApiResponse<{ reply: string; session_id?: string; model_used?: string; is_async?: boolean; task_id?: string }>> =>
    api.post('/conversations/send-message', data),
  
  // 获取对话历史（旧接口，兼容保留）
  getConversationHistory: (marketId: string, userId: string): Promise<ApiResponse<any[]>> =>
    api.get(`/conversations/history/${marketId}/${userId}`),
  
  // 保存对话
  saveConversation: (data: any): Promise<ApiResponse<any>> =>
    api.post('/conversations/save', data),
  
  // 会话管理
  getSessionList: (marketId: string, userId: string): Promise<ApiResponse<any[]>> =>
    api.get(`/conversations/sessions/list/${marketId}/${userId}`),
  
  createSession: (data: { user_id: string; market_id: string }): Promise<ApiResponse<any>> =>
    api.post('/conversations/sessions', data),
  
  deleteSession: (sessionId: string): Promise<ApiResponse<any>> =>
    api.delete(`/conversations/sessions/${sessionId}`),
  
  getSessionHistory: (sessionId: string): Promise<ApiResponse<any[]>> =>
    api.get(`/conversations/sessions/${sessionId}/history`),
  
  updateSessionTitle: (sessionId: string, title: string): Promise<ApiResponse<any>> =>
    api.put(`/conversations/sessions/${sessionId}/title`, { title }),
  
  // 更新会话中的任务状态（用于标记任务结果已清理）
  updateTaskStatus: (sessionId: string, taskId: string, status: string): Promise<ApiResponse<any>> =>
    api.put(`/conversations/sessions/${sessionId}/task-status`, { task_id: taskId, status }),
  
  // 批量检查任务是否存在
  checkTasksExist: (taskIds: string[]): Promise<ApiResponse<Array<{ task_id: string; exists: boolean }>>> =>
    api.post('/conversations/check-tasks-exist', { task_ids: taskIds }),
};

// 模型配置相关API
export const modelConfigApi = {
  // 获取mention列表
  getMentions: (): Promise<ApiResponse<any[]>> =>
    api.get('/model-config/mentions'),
};

// 工作流模型映射相关API（用于闭源模型渠道选择）
export const workflowModelApi = {
  // 获取指定工作流市场的模型渠道列表
  getMarketModels: (marketId: string): Promise<ApiResponse<WorkflowModelMapping[]>> =>
    api.get(`/workflow-markets/${marketId}/models`),
};

// 用户积分相关API
export const userPointsApi = {
  // 获取用户积分信息
  getUserPoints: (userId: string): Promise<ApiResponse<UserPointsInfo>> =>
    api.get(`/users/${userId}/points`),
};

// API Provider 任务相关API（apisource 模型）
export const apiTaskApi = {
  // 获取可用渠道分组列表（按 modality 过滤：image/video/chat）
  getCallGroups: (modality?: string): Promise<ApiResponse<CallGroup[]>> =>
    api.get('/api-tasks/call-groups', { params: modality ? { modality } : {} }),

  // 创建 API 任务
  createApiTask: (data: {
    market_id: string;
    user_id: string;
    param_data: Record<string, string>;
    session_id: string | null;
    group_id: string;
    display_params?: Record<string, string>;
    is_batch?: boolean;
    batch_index?: number;
  }): Promise<ApiResponse<{
    task_id: string;
    task_name: string;
    status: string;
    result_info: string | null;
    error_msg: string | null;
    cost_points: number;
    cost_cny: number;
  }>> => {
    const requestConfig = { timeout: 0, __skipRetry: true };
    return api.post('/api-tasks/create', data, requestConfig);
  },
};

export default api;
